<script>
/* global axios */
import WootMessageEditor from '../WootWriter/Editor.vue';
import TranslationEditor from './TranslationEditor.vue';
import ConversationLingxingOrdersAPI from 'dashboard/api/conversationLingxingOrders';
import { LANGUAGES, readDraft, isCurrent, acceptsTranslation } from 'dashboard/helper/bilingualDraft';

export default {
  components: { WootMessageEditor, TranslationEditor },
  props: {
    draftKey: { type: String, required: true },
    accountId: { type: [String, Number], required: true },
    conversationId: { type: Number, required: true },
    replyToId: { type: Number, default: null },
    disabled: Boolean,
    enterToSend: Boolean,
    suggestionOpen: Boolean,
    maxLength: { type: Number, default: 12000 },
    variables: { type: Object, default: () => ({}) },
    channelType: { type: String, default: '' },
    medium: { type: String, default: '' },
    send: { type: Function, required: true },
  },
  emits: ['ready', 'sending', 'focus', 'blur', 'toggle-canned-menu', 'toggle-variables-menu'],
  data() {
    return { draft: readDraft(this.$store.getters['draftMessages/get'](this.draftKey)), languages: LANGUAGES,
      translating: false, sending: false, error: '', generation: 0, controller: null, insertion: '', activePane: 'source', alive: true };
  },
  computed: {
    languageContextKey() {
      return `${this.accountId}:${this.conversationId}`;
    },
    conversationLanguage() {
      if (String(this.$store.getters.getCurrentAccountId) !== String(this.accountId)) return '';
      const context = ConversationLingxingOrdersAPI.getCountryContext(this.conversationId);
      const language = context?.customer_language || context;
      return language?.source === 'language' && Object.hasOwn(this.selectableLanguages, language.language_code)
        ? language.language_code : '';
    },
    selectableLanguages() {
      return Object.fromEntries(Object.entries(this.languages).filter(([code]) => code !== 'auto'));
    },
    displayedLanguage: {
      get() {
        const code = this.draft.language === 'auto' ? this.draft.detected : this.draft.language;
        return Object.hasOwn(this.selectableLanguages, code) ? code : this.conversationLanguage;
      },
      set(code) {
        if (Object.hasOwn(this.selectableLanguages, code)) this.draft.language = code;
      },
    },
    persistedDraft() { return this.$store.getters['draftMessages/get'](this.draftKey); },
    current() { return isCurrent(this.draft); },
    ready() { return this.current && !this.translating && !this.sending && !this.draft.attempted && !this.disabled && this.draft.text.length <= this.maxLength; },
    stale() { return !!this.draft.text && !this.current; },
  },
  watch: {
    languageContextKey: { immediate: true, handler() { this.loadConversationLanguage(); } },
    persistedDraft(value, previous) {
      if (!this.sending && this.draft.attempted && this.draft.attemptId
          && readDraft(previous).attemptId === this.draft.attemptId) this.draft = readDraft(value);
    },
    draft: { deep: true, flush: 'sync', handler() { this.saveDraft(); } },
    ready: { immediate: true, handler(value) { this.$emit('ready', value, this.draftKey); } },
    replyToId() { this.cancelTranslation(); this.draft.translatedSource = null; },
    'draft.source'() { this.cancelTranslation(); },
    'draft.language'() { this.cancelTranslation(); },
  },
  beforeUnmount() { this.alive = false; this.cancelTranslation(); this.saveDraft(); },
  methods: {
    async loadConversationLanguage() {
      if (String(this.$store.getters.getCurrentAccountId) !== String(this.accountId)) return;
      // Reuse the sidebar's account-scoped cache and in-flight request. Never
      // copy a late response into this draft or change its translation state.
      if (ConversationLingxingOrdersAPI.getCached(this.conversationId)) return;
      try { await ConversationLingxingOrdersAPI.getForConversation(this.conversationId); }
      catch { /* No context: preserve manual selection and automatic translation. */ }
    },
    saveDraft() { this.$store.dispatch('draftMessages/set', { key: this.draftKey, message: JSON.stringify(this.draft) }); },
    cancelTranslation() { this.generation += 1; this.controller?.abort(); this.controller = null; this.translating = false; },
    insert(content) { this.insertion = content; this.$refs.sourceEditor?.focusEditorInputField(); },
    paneFocus(pane) { this.activePane = pane; this.$emit('focus'); },
    insertEmoji(content) {
      if (this.disabled || this.sending || (this.activePane === 'target' && this.translating)) return;
      const ref = this.activePane === 'target' ? this.$refs.targetEditor : this.$refs.sourceEditor;
      ref?.insertPlainText(content);
    },
    handleEnter(event) {
      if (!event.target.closest?.('.ProseMirror') || event.isComposing || event.keyCode === 229 || this.suggestionOpen) return;
      const submitKey = !event.shiftKey && !event.altKey && (this.enterToSend ? !event.ctrlKey && !event.metaKey : event.ctrlKey || event.metaKey);
      if (submitKey) { event.preventDefault(); event.stopPropagation(); this.submit(); }
    },
    async translate() {
      if (this.translating || this.sending || this.disabled || !this.draft.source.trim() || this.draft.source.length > 12000) return;
      this.cancelTranslation();
      const generation = this.generation;
      const snapshot = { source: this.draft.source, language: this.draft.language };
      this.draft.translatedSource = null;
      this.controller = new AbortController(); this.translating = true; this.error = '';
      try {
        const { data } = await axios.post(`/api/v1/accounts/${this.accountId}/conversations/${this.conversationId}/draft_translation`,
          { content: snapshot.source, target_language: snapshot.language, reply_to_id: this.replyToId }, { signal: this.controller.signal, timeout: 45000 });
        if (!acceptsTranslation(this.draft, snapshot, generation, this.generation)) return;
        if (!data.text?.trim() || !this.languages[data.language] || data.language === 'auto') throw new Error('invalid');
        Object.assign(this.draft, { text: data.text, detected: data.language, translatedSource: snapshot.source, translatedLanguage: snapshot.language });
      } catch (error) {
        if (generation !== this.generation) return;
        this.error = error.response?.data?.error || '翻译失败，草稿已保留，请重试';
      } finally { if (generation === this.generation) this.translating = false; }
    },
    async submit() {
      if (!this.ready) return;
      this.sending = true; this.error = ''; this.$emit('sending', true, this.draftKey);
      // Persist before dispatch so refresh cannot silently enable a duplicate submission.
      this.draft.attemptId = crypto.randomUUID();
      this.draft.attempted = true;
      const key = this.draftKey;
      const pending = JSON.stringify(this.draft);
      const completed = { ...readDraft('{}'), language: this.draft.language, detected: this.draft.detected };
      const persistOutcome = draft => {
        if (this.$store.getters['draftMessages/get'](key) !== pending) return;
        this.$store.dispatch('draftMessages/set', { key, message: JSON.stringify(draft) });
        if (this.alive) this.draft = draft;
      };
      try {
        await this.send(this.draft.text);
        persistOutcome(completed);
      } catch (error) {
        if (error.notSubmitted) persistOutcome({ ...readDraft(pending), attempted: false, attemptId: null });
        this.error = error.message || '发送未确认，请检查聊天记录中的消息状态';
      } finally { this.sending = false; this.$emit('sending', false, key); }
    },
    newDraft() { if (!this.sending) { this.draft = { ...readDraft('{}'), language: this.draft.language }; this.error = ''; } },
  },
};
</script>

<template>
  <div class="bcw-bilingual" @keydown.enter.capture="handleEnter" @keydown.enter.stop>
    <section class="bcw-source" aria-label="中文草稿">
      <h3>中文草稿</h3>
      <WootMessageEditor class="input popover-prosemirror-menu bcw-source-editor" ref="sourceEditor" v-model="draft.source" :editor-id="draftKey" :conversation-id="conversationId"
        :disabled="disabled || sending" override-line-breaks placeholder="输入中文，点击中间按钮翻译" :variables="variables" enable-variables
        :channel-type="channelType" :medium="medium" :update-selection-with="insertion" :focus-on-mount="false"
        @clear-selection="insertion = ''" @focus="paneFocus('source')" @blur="$emit('blur')"
        @toggle-canned-menu="$emit('toggle-canned-menu', $event)" @toggle-variables-menu="$emit('toggle-variables-menu', $event)" />
    </section>
    <div class="bcw-translate-divider">
      <button type="button" class="bcw-translate-button" :disabled="disabled || translating || sending || !draft.source.trim() || draft.source.length > 12000"
        :aria-busy="translating" @click="translate">
        <span class="bcw-translate-label">{{ translating ? '翻译中' : '翻译' }}</span><span v-if="!translating" class="i-lucide-arrow-right" aria-hidden="true" />
      </button>
    </div>
    <section class="bcw-target" aria-label="译文预览">
      <div class="bcw-target-heading"><label :for="`${draftKey}-language`">客户语言：</label>
        <select :id="`${draftKey}-language`" v-model="displayedLanguage" :disabled="sending || disabled" aria-label="客户语言">
          <option v-if="!displayedLanguage" disabled value="">{{ translating ? '识别中' : '待识别' }}</option>
          <option v-for="(label, code) in selectableLanguages" :key="code" :value="code">{{ label }}</option>
        </select>
      </div>
      <TranslationEditor ref="targetEditor" :handle-submit-shortcut="false" v-model="draft.text" :editor-id="`${draftKey}-translation`"
        placeholder="点击翻译后，译文将显示在这里"
        :disabled="disabled || sending || translating" @focus="paneFocus('target')" @blur="$emit('blur')"
        @submit="submit" />
    </section>
    <div v-if="error || stale || draft.attempted || draft.text.length > maxLength || draft.source.length > 12000" class="bcw-translation-status" role="status" aria-live="polite">
      <span v-if="draft.attempted">本次发送已提交，请先检查聊天记录中的消息状态；失败时从原消息重试。</span>
      <span v-else-if="error">{{ error }}</span>
      <span v-else-if="draft.source.length > 12000 || draft.text.length > maxLength">内容过长，请缩短后重试。</span>
      <span v-else-if="stale">中文或目标语言已更改，请点击“翻译”更新译文。</span>
      <button v-if="draft.attempted && !sending" type="button" @click="newDraft">开始新回复</button>
    </div>
  </div>
</template>

<style scoped>
.bcw-bilingual { display:grid; flex:1 1 0; min-height:0; grid-template-columns:minmax(0,1fr) 48px minmax(0,1fr); grid-template-rows:minmax(0,1fr) auto; gap:0; padding-top:8px; }
.bcw-source,.bcw-target { min-width:0; min-height:0; display:flex; flex-direction:column; }
.bcw-source { padding-right:12px; }.bcw-target { padding-left:12px; }
h3,.bcw-target-heading { flex-shrink:0; margin:0 0 8px; font-size:14px; font-weight:600; line-height:24px; }
.bcw-target-heading { display:flex; align-items:center; flex-wrap:wrap; }
select { height:24px; min-height:24px; outline:none; font-weight:600; width:auto; max-width:140px; margin:0; padding:0 18px 0 0; border:0; background-color:transparent; font-size:14px; color:inherit; }
.bcw-translate-divider { position:relative; display:flex; align-items:center; justify-content:center; }
.bcw-translate-divider::before { content:''; position:absolute; top:0; bottom:8px; width:1px; background:var(--color-slate-5,#e2e8f0); }
.bcw-translate-button { position:relative; z-index:1; display:flex; flex-direction:column; gap:6px; align-items:center; justify-content:center; width:36px; min-width:36px; min-height:0; max-height:100%; padding:8px 6px; border:1px solid #bfdbfe; border-radius:999px; background:#eff6ff; color:#2563eb; font-size:12px; font-weight:500; white-space:nowrap; }
.bcw-translate-label { writing-mode:vertical-rl; text-orientation:upright; line-height:16px; letter-spacing:2px; }
.bcw-translate-button:hover:not(:disabled) { background:#dbeafe; }.bcw-translate-button:disabled { opacity:.55; cursor:not-allowed; }.bcw-translate-button span:last-child { flex-shrink:0; }
.bcw-translate-button:focus-visible,select:focus-visible { outline:2px solid #3b82f6; outline-offset:3px; }
.bcw-translation-status { grid-column:1/-1; font-size:12px; color:#b45309; padding:6px 0; }
.bcw-translation-status button { margin-left:8px; text-decoration:underline; }
.bcw-source-editor { flex:1 1 0; min-height:0; display:flex; flex-direction:column; }
/* Carry the available height through the editor mount and menu wrapper. */
.bcw-source-editor :deep(.woot-editor-content),
.bcw-source-editor :deep(.ProseMirror-menubar-wrapper) {
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-height: 0;
  min-width: 0;
}
.bcw-source-editor :deep(.ProseMirror-woot-style) { flex:1 1 0; min-height:0 !important; max-height:none !important; overflow-y:auto; overscroll-behavior:contain; }
.bcw-source :deep(.ProseMirror) { min-height:0; font-size:14px; }
@media(max-width:700px) { .bcw-bilingual { grid-template-columns:minmax(0,1fr) 40px minmax(0,1fr); }.bcw-source { padding-right:6px; }.bcw-target { padding-left:6px; }.bcw-target-heading { font-size:12px; } select { max-width:100%; font-size:12px; } }
</style>
